import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, getErrorMessage } from '../api/client';
import type { Me, User } from '../api/types';

function NameCell({ user, onSave, saving }: { user: User; onSave: (name: string) => void; saving: boolean }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(user.display_name ?? '');

  if (!editing) {
    return (
      <span className="flex items-center gap-2">
        {user.display_name || <span className="text-gray-400">—</span>}
        <button
          className="text-xs text-navy hover:underline"
          onClick={() => {
            setValue(user.display_name ?? '');
            setEditing(true);
          }}
        >
          edit
        </button>
      </span>
    );
  }

  return (
    <span className="flex items-center gap-2">
      <input
        className="rounded border px-2 py-0.5 text-sm"
        value={value}
        autoFocus
        onChange={(e) => setValue(e.target.value)}
      />
      <button
        className="text-xs font-semibold text-navy hover:underline disabled:opacity-50"
        disabled={saving}
        onClick={() => {
          onSave(value.trim());
          setEditing(false);
        }}
      >
        save
      </button>
      <button className="text-xs text-gray-500 hover:underline" onClick={() => setEditing(false)}>
        cancel
      </button>
    </span>
  );
}

export function Users() {
  const qc = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<Me>('/api/admin/me') });
  const users = useQuery({ queryKey: ['users'], queryFn: () => api<{ users: User[] }>('/api/admin/users') });

  const patch = useMutation({
    mutationFn: ({ id, body }: { id: string; body: { isAdmin?: boolean; displayName?: string } }) =>
      api(`/api/admin/users/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['users'] }),
  });

  return (
    <div>
      <h1 className="mb-4 text-xl font-bold text-navy">Users</h1>

      {patch.isError ? (
        <p className="mb-3 text-sm text-red-600">{getErrorMessage(patch.error)}</p>
      ) : null}

      <div className="overflow-hidden rounded border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-gray-50 text-left text-xs text-gray-500">
              <th className="p-2">Email</th>
              <th className="p-2">Display name</th>
              <th className="p-2">Admin</th>
              <th className="p-2">Last login</th>
            </tr>
          </thead>
          <tbody>
            {users.data?.users.map((u) => {
              const isSelf = me.data?.id === u.id;
              return (
                <tr key={u.id} className="border-b">
                  <td className="p-2">{u.email || <span className="text-gray-400">—</span>}</td>
                  <td className="p-2">
                    <NameCell
                      user={u}
                      saving={patch.isPending}
                      onSave={(name) => patch.mutate({ id: u.id, body: { displayName: name } })}
                    />
                  </td>
                  <td className="p-2">
                    <input
                      type="checkbox"
                      checked={u.is_admin}
                      disabled={isSelf || patch.isPending}
                      title={isSelf ? 'You cannot change your own admin status' : undefined}
                      onChange={(e) => patch.mutate({ id: u.id, body: { isAdmin: e.target.checked } })}
                    />
                  </td>
                  <td className="p-2 text-xs text-gray-500">
                    {u.last_login_utc ? new Date(u.last_login_utc).toLocaleString() : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {users.isLoading ? <div className="p-3 text-sm">Loading…</div> : null}
        {users.isError ? <div className="p-3 text-sm text-red-600">{getErrorMessage(users.error)}</div> : null}
      </div>
    </div>
  );
}
